<template>
  <div class="list-sort">
    <template v-if="currentDevice.isDesktop">
      <p id="sort-label" class="list-sort__title">{{ i18n.orderBy.title }}:</p>
      <div class="list-sort__dropdown" :class="tippyState">
        <tippy
          allow-html
          interactive
          trigger="click"
          theme="light"
          placement="bottom"
          @show="handleTippyShow"
          @hide="handleTippyHide"
        >
          <template #default="{ show, hide }">
            <button
              id="sort-trigger"
              class="list-sort__dropdown-trigger"
              aria-controls="sort-options"
              type="button"
              aria-haspopup="listbox"
              :aria-expanded="isTippyOpen ? 'true' : 'false'"
              aria-labelledby="sort-label sort-trigger"
              @keydown="handleSortKeydown(show, hide, $event)"
            >
              {{ i18n.orderBy.options[currentSort.name] }}
            </button>
          </template>

          <template #content="{ hide }">
            <ul
              id="sort-options"
              class="list-sort__dropdown-list"
              role="listbox"
              aria-labelledby="sort-label"
              tabindex="-1"
              :hidden="!isTippyOpen"
            >
              <li
                v-for="(option, index) in sortOptions"
                :id="`sort-option-${option.name}`"
                :key="option.name"
                role="option"
                class="list-sort__dropdown-item"
                :aria-selected="currentSort.name === option.name ? 'true' : 'false'"
                :tabindex="currentSort.name === option.name ? 0 : -1"
                @keydown="handleOptionKeydown($event, option, index, hide)"
                @click="
                  () => {
                    hide()
                    changeSort(option.field, option.direction)
                  }
                "
                @keydown.enter.prevent="
                  () => {
                    hide()
                    changeSort(option.field, option.direction)
                  }
                "
                @keydown.space.prevent="
                  () => {
                    hide()
                    changeSort(option.field, option.direction)
                  }
                "
              >
                {{ i18n.orderBy.options[option.name] }}
              </li>
            </ul>
          </template>
        </tippy>
      </div>
    </template>
    <template v-else-if="(currentDevice.isTablet || currentDevice.isMobile) && !removeFilters && filters !== null">
      <!-- BottomSheet> -->
      <BottomSheet
        sheet-id="list-sort-bottom-sheet"
        sheet-class="list-sort__bottom-sheet full-page"
        :is-open="isBottomSheetOpen"
        :title="i18n.orderBy.modalTitle"
        @on-close="isBottomSheetOpen = false"
      >
        <template #trigger>
          <button
            id="sort-label"
            type="button"
            class="list-sort__title"
            aria-haspopup="dialog"
            :aria-expanded="isBottomSheetOpen ? 'true' : 'false'"
            @click="openMobileFiltersBottomSheet"
          >
            <span class="s s-filter mr-2"></span>
            {{ i18n.orderBy.filterBy }}
          </button>
        </template>
        <template #content>
          <ListFilters
            :filters="filters"
            :mobile-filters-state="tempMobileFilters"
            @update:mobile-filters-state="updateTempMobileFilters"
          />
          <div class="bottom-sheet__footer js-sheet-footer w-100">
            <div class="bottom-sheet__footer--ctas js-sheet-footer-ctas">
              <button class="btn-secondary w-100 utag-delete-filters js-delete-filters" @click="resetMobileFilters">
                {{ i18n.commons.clear_filters }}
              </button>
              <button class="btn-primary w-100 mr-2 utag-apply-filters js-apply-filters" @click="applyMobileFilters">
                {{ i18n.commons.apply_filters }}
              </button>
            </div>
          </div>
        </template>
      </BottomSheet>
    </template>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, inject, nextTick, ref } from 'vue'
import { Tippy } from 'vue-tippy'

import { currentDevice } from '../../../../../core/utils/currentDevice'
import { useHotelsInfoStore } from '../../../blocks/HotelsInfo/store/hotels_info'
import { loadStyles } from '../../../mixins/LoadStyles/LoadStyles'
import BottomSheet from '../../BottomSheet/BottomSheet.vue'
import { emitUtagEvent } from '../HotelsInfo.analytics'
import ListFilters from '../ListFilters/ListFilters'

import sortOptions from './sortOptions'

/**
 * Styles
 */
const COMPONENT_NAME = 'ListSort'
loadStyles({ name: COMPONENT_NAME, folder: 'components/HotelsInfo' })

/**
 * Inject
 */
const i18n = inject('i18n')

/**
 * Store
 */
const hotelsInfoStore = useHotelsInfoStore()
const { sort, filters: currentStoreFilters } = storeToRefs(hotelsInfoStore)
hotelsInfoStore.setSort(sortOptions[0].field, sortOptions[0].direction)

/**
 * Props
 */
const props = defineProps({
  filters: {
    type: Object,
    default: () => ({}),
  },
  removeFilters: {
    type: Boolean,
    default: false,
  },
})

/**
 * Data
 */
const tippyState = ref('')
const isTippyOpen = ref(false)

const isBottomSheetOpen = ref(false)
const tempMobileFilters = ref({})

/**
 * Computed
 */
const currentSort = computed(() => {
  return sortOptions.find(option => option.field === sort.value.field && option.direction === sort.value.direction)
})

/**
 * Methods
 */
const changeSort = (field, direction) => {
  hotelsInfoStore.setSort(field, direction)
  emitUtagEvent({
    event_name: 'sort_by',
    event_cat: 'destinations',
    event_act: 'sort by',
    event_lbl: field,
    event_purpose: 'inspiration',
    event_structure: 'body',
  })
}

const openMobileFiltersBottomSheet = () => {
  tempMobileFilters.value = JSON.parse(JSON.stringify(currentStoreFilters.value))
  isBottomSheetOpen.value = true
}

const updateTempMobileFilters = newFiltersState => {
  tempMobileFilters.value = newFiltersState
}

const applyMobileFilters = () => {
  for (const key in tempMobileFilters.value) {
    if (Object.prototype.hasOwnProperty.call(tempMobileFilters.value, key)) {
      hotelsInfoStore.replaceFilters(key, tempMobileFilters.value[key])
    }
  }
  isBottomSheetOpen.value = false
  emitUtagEvent({
    event_name: 'filters',
    event_cat: 'filters destinations',
    event_act: 'apply filters',
    event_lbl: '',
    event_structure: 'body',
    event_purpose: 'inspiration',
  })
}

const resetMobileFilters = () => {
  hotelsInfoStore.resetFilters()
  tempMobileFilters.value = JSON.parse(JSON.stringify(hotelsInfoStore.filters))
  isBottomSheetOpen.value = false
  emitUtagEvent({
    event_name: 'filters',
    event_cat: 'filters destinations',
    event_act: 'clear filters',
    event_lbl: '',
    event_structure: 'body',
    event_purpose: 'inspiration',
  })
}

function handleTippyShow() {
  tippyState.value = 'list-sort__dropdown--active'
  isTippyOpen.value = true
  nextTick(() => {
    // Mover el foco a la opción actualmente seleccionada cuando el dropdown se abre
    const selectedOption = document.getElementById(`sort-option-${currentSort.value.name}`)
    if (selectedOption) {
      selectedOption.focus()
    }
  })
}

function handleTippyHide() {
  tippyState.value = ''
  isTippyOpen.value = false
  nextTick(() => {
    // Mover el foco de vuelta al botón disparador cuando el dropdown se cierra
    const triggerButton = document.getElementById('sort-trigger')
    if (triggerButton) {
      triggerButton.focus()
    }
  })
}

function handleSortKeydown(event) {
  const triggerButton = event.currentTarget

  switch (event.key) {
    case 'ArrowDown':
    case 'Enter':
    case ' ':
      event.preventDefault()
      if (!isTippyOpen.value) {
        triggerButton._tippy.show()
      }
      break
    case 'Escape':
      event.preventDefault()
      if (isTippyOpen.value) {
        triggerButton._tippy.hide()
      }
      break
  }
}

function handleOptionKeydown(event, option, index, hideTippy) {
  const options = Array.from(event.currentTarget.closest('[role="listbox"]').querySelectorAll('[role="option"]'))
  let nextIndex = index

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      nextIndex = (index + 1) % options.length
      break
    case 'ArrowUp':
      event.preventDefault()
      nextIndex = (index - 1 + options.length) % options.length
      break
    case 'Home':
      event.preventDefault()
      nextIndex = 0
      break
    case 'End':
      event.preventDefault()
      nextIndex = options.length - 1
      break
    case 'Enter':
    case ' ':
      event.preventDefault()

      changeSort(option.field, option.direction)
      hideTippy()
      return
    case 'Escape':
      event.preventDefault()
      hideTippy()
      return
    default:
      return
  }

  // Mover el foco a la nueva opción
  if (options[nextIndex]) {
    options[nextIndex].focus()
  }
}
</script>
